import { mathService } from '@approvalmax/utils';
import uniq from 'lodash/uniq';
import uniqueId from 'lodash/uniqueId';
import { domain } from 'modules/data';
import { XeroContext } from 'pages/requestForm/data/xero/XeroContext';
import { ImportManualJournalLine, ImportManualJournalTracking } from 'shared/data/webApp/v1';

import { messages } from './XeroManualJournalRequestForm.messages';
import { ExpandedXeroManualJournalLine, ManualJournalFields } from './XeroManualJournalRequestForm.types';

export const createNewManualJournalLine = (
    defaultNarrationToDescription?: string | null
): ExpandedXeroManualJournalLine => {
    return {
        id: uniqueId('xeroManualJournalLine-'),
        amountType: null,
        description: defaultNarrationToDescription || '',
        tracking: [],
        isInventory: false,
        isEmpty: true,
    };
};

export const expandManualJournalLine = (line: domain.XeroManualJournalLine): ExpandedXeroManualJournalLine => {
    return {
        ...line,
        id: line.id || uniqueId('xeroManualJournalLine-'),
        isEmpty: checkManualJournalLineIsEmpty(line),
    };
};

export const checkManualJournalLineIsEmpty = (line: domain.XeroManualJournalLine) => {
    return !line.account && typeof line.amount !== 'number' && !line.description;
};

export const checkManualJournalLinesAreEmpty = (lines: domain.XeroManualJournalLine[]) => {
    return lines.every((line) => checkManualJournalLineIsEmpty(line));
};

export const checkManualJournalLinesAreInvalid = (
    lines: domain.XeroManualJournalLine[],
    lineAmountType: domain.LineAmountType,
    requiredTrackingCategories: string[]
): boolean => {
    const hasInvalidLine = lines.find((line) => {
        if (checkManualJournalLineIsEmpty(line)) {
            return false;
        }

        if (!line.account || typeof line.amount !== 'number') {
            return true;
        }

        if (lineAmountType !== domain.LineAmountType.NoTax && !line.tax) {
            return true;
        }

        if (line.tax && lineAmountType !== domain.LineAmountType.NoTax && typeof line.taxAmount !== 'number') {
            return true;
        }

        const allRequiredTrackingFieldsAreFilled = requiredTrackingCategories.every((trackingCategoryId) => {
            const fieldValue = line.tracking.find((item) => item.category.id === trackingCategoryId);

            return Boolean(fieldValue?.value);
        });

        if (!allRequiredTrackingFieldsAreFilled) {
            return true;
        }

        return false;
    });

    return Boolean(hasInvalidLine);
};

export const getValidationErrors = (
    fields: ManualJournalFields,
    lines: domain.XeroManualJournalLine[],
    requiredFieldIds: string[],
    trackingCategories: XeroContext['trackingCategories']
): string[] => {
    const errors: string[] = [];

    const requiredTrackingCategories = trackingCategories.reduce<string[]>((idList, category) => {
        if (requiredFieldIds.includes(category.category.id)) {
            idList.push(category.category.id);
        }

        return idList;
    }, []);

    const areLinesInvalid = checkManualJournalLinesAreInvalid(lines, fields.lineAmountType, requiredTrackingCategories);

    if (!fields.journalDate || !fields.narration || areLinesInvalid) {
        errors.push(messages.mandatoryFieldsError);
    }

    return uniq(errors);
};

export const calculateAmounts = (lines: domain.XeroManualJournalLine[], lineAmountType: domain.LineAmountType) => {
    const result: {
        debitSubTotal: number;
        creditSubTotal: number;
        debitTotal: number;
        creditTotal: number;
        taxes: Record<
            string,
            {
                taxName: string;
                debit: number;
                credit: number;
                taxableDebitAmount: number;
                taxableCreditAmount: number;
                taxCodeId: string;
                taxPercent: number;
            }
        >;
        debitTotalTax: number;
        creditTotalTax: number;
    } = {
        debitSubTotal: 0,
        creditSubTotal: 0,
        debitTotal: 0,
        creditTotal: 0,
        taxes: {},
        debitTotalTax: 0,
        creditTotalTax: 0,
    };

    lines.forEach((line) => {
        const lineAmount = mathService.round(line.amount || 0, 2);

        if (lineAmountType === domain.LineAmountType.NoTax || !line.tax) {
            if (line.amountType === domain.XeroManualJournalLineAmountType.Debit) {
                result.debitSubTotal = mathService.add(result.debitSubTotal, lineAmount || 0);
                result.debitTotal = mathService.add(result.debitTotal, lineAmount || 0);
            }

            if (line.amountType === domain.XeroManualJournalLineAmountType.Credit) {
                result.creditSubTotal = mathService.add(result.creditSubTotal, lineAmount || 0);
                result.creditTotal = mathService.add(result.creditTotal, lineAmount || 0);
            }

            return;
        }

        if (line.tax) {
            const taxAmount = line.taxAmount ?? 0;

            const taxKey = line.tax.id;

            if (!result.taxes[taxKey]) {
                result.taxes[taxKey] = {
                    debit: 0,
                    credit: 0,
                    taxableDebitAmount: 0,
                    taxableCreditAmount: 0,
                    taxName: line.tax.text,
                    taxCodeId: line.tax.id,
                    taxPercent: line.tax.rate,
                };
            }

            if (line.amountType === domain.XeroManualJournalLineAmountType.Debit) {
                result.taxes[taxKey].debit = mathService.round(
                    mathService.add(result.taxes[taxKey].debit, taxAmount),
                    2
                );

                result.taxes[taxKey].taxableDebitAmount = mathService.round(
                    mathService.add(result.taxes[taxKey].taxableDebitAmount, lineAmount),
                    2
                );

                result.debitSubTotal = mathService.add(result.debitSubTotal, lineAmount);

                result.debitTotal = mathService.add(result.debitTotal, lineAmount);

                if (lineAmountType === domain.LineAmountType.TaxExclusive) {
                    result.debitTotal = mathService.add(result.debitTotal, taxAmount);
                }
            }

            if (line.amountType === domain.XeroManualJournalLineAmountType.Credit) {
                result.taxes[taxKey].credit = mathService.round(
                    mathService.add(result.taxes[taxKey].credit, taxAmount),
                    2
                );

                result.taxes[taxKey].taxableCreditAmount = mathService.round(
                    mathService.add(result.taxes[taxKey].taxableCreditAmount, lineAmount),
                    2
                );

                result.creditSubTotal = mathService.add(result.creditSubTotal, lineAmount);

                result.creditTotal = mathService.add(result.creditTotal, lineAmount);

                if (lineAmountType === domain.LineAmountType.TaxExclusive) {
                    result.creditTotal = mathService.add(result.creditTotal, taxAmount);
                }
            }
        }
    });

    result.debitTotalTax = Object.values(result.taxes).reduce((acc, taxValue) => {
        return mathService.add(acc, taxValue.debit);
    }, 0);

    result.creditTotalTax = Object.values(result.taxes).reduce((acc, taxValue) => {
        return mathService.add(acc, taxValue.credit);
    }, 0);

    result.debitSubTotal = mathService.round(result.debitSubTotal, 2);
    result.debitTotal = mathService.round(result.debitTotal, 2);
    result.creditSubTotal = mathService.round(result.creditSubTotal, 2);
    result.creditTotal = mathService.round(result.creditTotal, 2);
    result.debitTotalTax = mathService.round(result.debitTotalTax, 2);
    result.creditTotalTax = mathService.round(result.creditTotalTax, 2);

    return result;
};

export const calculateLineTaxAmount = (amount: number, taxRate: number, lineAmountType: domain.LineAmountType) => {
    const taxableAmount =
        (amount * 100) / (100 + (lineAmountType === domain.LineAmountType.TaxExclusive ? 0 : taxRate));

    return Math.round(taxableAmount * taxRate) / 100;
};

export const mapManualJournalLines = (
    lineItems: ImportManualJournalLine[],
    lineAmountType: domain.LineAmountType,
    trackingCategories: XeroContext['trackingCategories'],
    taxCodes: domain.XeroTaxCode[]
): domain.XeroManualJournalLine[] => {
    return lineItems.map((lineItem) => {
        const tax = getTaxByName(lineItem.taxRateString, taxCodes);

        let taxAmount = 0;

        if (typeof lineItem.amount === 'number' && tax && lineAmountType !== domain.LineAmountType.NoTax) {
            taxAmount = calculateLineTaxAmount(lineItem.amount, tax.rate, lineAmountType);
        }

        return {
            id: uniqueId('xeroManualJournalLine-'),
            description: lineItem.description || '',
            tracking: mapManualJournalTracking(lineItem.tracking, trackingCategories),
            tax,
            taxAmount,
            amount: lineItem.amount,
            account: lineItem.accountId
                ? {
                      id: lineItem.accountId,
                      text: lineItem.account,
                  }
                : undefined,
            amountType: lineItem.amountSide,
            isInventory: false,
        };
    });
};

export const mapManualJournalTracking = (
    tracking: ImportManualJournalTracking[],
    trackingCategories: XeroContext['trackingCategories']
) => {
    return tracking.reduce<domain.XeroLineItem['tracking']>((resultTracking, item) => {
        const currentCategory = trackingCategories.find(
            ({ category }) => category.text === item.categoryName || category.id === item.categoryId
        );

        const currentValue = currentCategory?.options.find(
            (option) => option.text === item.optionName || option.text === item.optionId
        );

        if (currentCategory && currentValue) {
            resultTracking.push({
                category: { ...currentCategory.category },
                value: { ...currentValue },
            });
        }

        return resultTracking;
    }, []);
};

export const getTaxByName = (
    taxRateString: string | null,
    taxCodes: domain.XeroTaxCode[]
): domain.XeroTaxCode | undefined => {
    if (taxRateString === null) {
        return;
    }

    return taxCodes.find((taxCode) => {
        const taxName = getTaxNameFromText(taxCode.text);

        return taxName === taxRateString.trim();
    });
};

const getTaxNameFromText = (str: string) => str.replace(/\(\d+(\.\d+)?%\)/g, '').trim();
